import { ResponseResolver, restContext, RestRequest } from 'msw';
import { delayedResponse } from '@/utils/useMswCustomResponse';
import { MONTHLY_APPLICATION_STATUS } from '../../constants/monthlyApplications';
import { ApplicationStatus } from '../types/monthlyApplication';
import { ResubmissionResponse, ResubmissionType } from '../types/resubmission';
import { ACCOUNT_TYPES } from '~/features/user/constants';

/**
 * 再提出依頼を取得
 */
const getResubmission: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  const { getAccountType } = useCognitoAuth();
  const accountType = await getAccountType();

  /** applicationId に基づいた契約ステータス */
  const status =
    MONTHLY_APPLICATION_STATUS[
      Object.keys(MONTHLY_APPLICATION_STATUS)[
        applicationId - 1
      ] as ApplicationStatus
    ];

  let resubmissionTypes: ResubmissionType[];
  let comment: string;

  // accountTypeとstatusの組み合わせで条件分岐
  if (accountType === ACCOUNT_TYPES.PERSONAL) {
    switch (status?.ID) {
      case MONTHLY_APPLICATION_STATUS
        .PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED.ID:
        // 個人仮申込の再提出
        resubmissionTypes = [
          'EMAIL',
          'VEHICLE_OWNERSHIP',
          'PARKED_VEHICLE_CATEGORY',
          'PARKED_VEHICLE_TYPE',
          'BIKE_DISPLACEMENT',
          'VEHICLE_NUMBER',
          'SCHEDULED_DELIVERY_DATE',
          'NO_VEHICLE_REASON',
          'VEHICLE_FRONT_IMAGE',
          'VEHICLE_BACK_IMAGE',
          'VEHICLE_SIDE_IMAGE',
          'LICENSE_FRONT_IMAGE',
          'LICENSE_BACK_IMAGE',
          'CAR_INSPECTION_IMAGE',
        ];
        comment =
          'これは個人仮申込の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      case MONTHLY_APPLICATION_STATUS.APPLICATION_RESUBMISSION_REQUESTED.ID:
        // 個人本申込の再提出
        resubmissionTypes = [
          'GENDER',
          'BIRTHDAY',
          'ZIP_CODE',
          'ADDRESS',
          'LANDLINE_PHONE_NUMBER',
          'MOBILE_PHONE_NUMBER',
          'JOB_TYPE',
          'WORKPLACE_COMPANY_NAME',
          'WORKPLACE_COMPANY_NAME_KANA',
          'WORKPLACE_ZIP_CODE',
          'WORKPLACE_ADDRESS',
          'WORKPLACE_PHONE_NUMBER',
          'WORKPLACE_INDUSTRY_CATEGORY',
          'ANNUAL_INCOME',
          'EMERGENCY_CONTACT_NAME',
          'EMERGENCY_CONTACT_NAME_KANA',
          'EMERGENCY_CONTACT_PHONE_NUMBER',
          'EMERGENCY_CONTACT_RELATIONSHIP',
        ];
        comment =
          '個人本申込の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      case MONTHLY_APPLICATION_STATUS.CONTRACT_UPDATE_RESUBMISSION_REQUESTED.ID:
        // 個人契約内容の更新
        resubmissionTypes = [
          'EMAIL',
          'VEHICLE_OWNERSHIP',
          'PARKED_VEHICLE_CATEGORY',
          'PARKED_VEHICLE_TYPE',
          'BIKE_DISPLACEMENT',
          'VEHICLE_NUMBER',
          'SCHEDULED_DELIVERY_DATE',
          'NO_VEHICLE_REASON',
          'VEHICLE_FRONT_IMAGE',
          'VEHICLE_BACK_IMAGE',
          'VEHICLE_SIDE_IMAGE',
          'LICENSE_FRONT_IMAGE',
          'LICENSE_BACK_IMAGE',
          'CAR_INSPECTION_IMAGE',
          'GENDER',
          'BIRTHDAY',
          'ZIP_CODE',
          'ADDRESS',
          'LANDLINE_PHONE_NUMBER',
          'MOBILE_PHONE_NUMBER',
          'JOB_TYPE',
          'WORKPLACE_COMPANY_NAME',
          'WORKPLACE_COMPANY_NAME_KANA',
          'WORKPLACE_ZIP_CODE',
          'WORKPLACE_ADDRESS',
          'WORKPLACE_PHONE_NUMBER',
          'WORKPLACE_INDUSTRY_CATEGORY',
          'ANNUAL_INCOME',
          'EMERGENCY_CONTACT_NAME',
          'EMERGENCY_CONTACT_NAME_KANA',
          'EMERGENCY_CONTACT_PHONE_NUMBER',
          'EMERGENCY_CONTACT_RELATIONSHIP',
          'LAST_NAME',
          'FIRST_NAME',
          'LAST_NAME_KANA',
          'FIRST_NAME_KANA',
        ];
        comment =
          'これは個人契約内容の更新の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      default:
        // デフォルトは個人仮申込の再提出
        resubmissionTypes = [];
        comment = '再提出ができない契約ステータスです。';
    }
  } else {
    // 法人アカウント
    switch (status?.ID) {
      case MONTHLY_APPLICATION_STATUS
        .PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED.ID:
        // 法人仮申込の再提出
        resubmissionTypes = [
          // 'EMAIL',
          // 'CORPORATE_NAME',
          // 'CORPORATE_NAME_KANA',
          // 'HEADQUARTERS_ADDRESS',
          // 'USAGE_PURPOSE',
          // 'PARKED_VEHICLE_CATEGORY',
          // 'PARKED_VEHICLE_TYPE',
          // 'BIKE_DISPLACEMENT',
          'VEHICLE_NUMBER',
          // 'SCHEDULED_DELIVERY_DATE',
          // 'NO_VEHICLE_REASON',
          // 'PLANNED_VEHICLE_COLOR',
          // 'VEHICLE_FRONT_IMAGE',
          // 'VEHICLE_BACK_IMAGE',
          // 'VEHICLE_SIDE_IMAGE',
          // 'COMPANY_REGISTRY_IMAGE',
          // 'CAR_INSPECTION_IMAGE',
        ];
        comment =
          'これは法人仮申込の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      case MONTHLY_APPLICATION_STATUS.APPLICATION_RESUBMISSION_REQUESTED.ID:
        // 法人本申込の再提出
        resubmissionTypes = [
          'HEADQUARTERS_PHONE_NUMBER',
          'INDUSTRY',
          'REPRESENTATIVE_NAME',
          'REPRESENTATIVE_NAME_KANA',
          'LISTING_STATUS',
          'DEPARTMENT_NAME',
          'CONTACT_PERSON_NAME',
          'CONTACT_PERSON_NAME_KANA',
          'DEPARTMENT_ADDRESS',
          'DEPARTMENT_PHONE_NUMBER',
        ];
        comment =
          'これは法人本申込の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      case MONTHLY_APPLICATION_STATUS.CONTRACT_UPDATE_RESUBMISSION_REQUESTED.ID:
        // 法人契約内容の更新
        resubmissionTypes = [
          'EMAIL',
          'CORPORATE_NAME',
          'CORPORATE_NAME_KANA',
          'HEADQUARTERS_ADDRESS',
          'USAGE_PURPOSE',
          'PARKED_VEHICLE_CATEGORY',
          'PARKED_VEHICLE_TYPE',
          'BIKE_DISPLACEMENT',
          'VEHICLE_NUMBER',
          'SCHEDULED_DELIVERY_DATE',
          'NO_VEHICLE_REASON',
          'PLANNED_VEHICLE_COLOR',
          'VEHICLE_FRONT_IMAGE',
          'VEHICLE_BACK_IMAGE',
          'VEHICLE_SIDE_IMAGE',
          'COMPANY_REGISTRY_IMAGE',
          'CAR_INSPECTION_IMAGE',
          'HEADQUARTERS_PHONE_NUMBER',
          'INDUSTRY',
          'REPRESENTATIVE_NAME',
          'REPRESENTATIVE_NAME_KANA',
          'LISTING_STATUS',
          'DEPARTMENT_NAME',
          'CONTACT_PERSON_NAME',
          'CONTACT_PERSON_NAME_KANA',
          'DEPARTMENT_ADDRESS',
          'DEPARTMENT_PHONE_NUMBER',
        ];
        comment =
          'これは法人契約内容の更新の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      default:
        // デフォルトは法人仮申込の再提出
        resubmissionTypes = [];
        comment = '再提出ができない契約ステータスです。';
    }
  }

  return delayedResponse(
    ctx.status(200),
    ctx.json<ResubmissionResponse>({
      comment,
      resubmissionTypes,
    }),
  );
};

const mockResubmission = {
  getResubmission,
};

export default mockResubmission;
